/**
 * Regras da tabela do Fluxo de Caixa (MAN-134), separadas da tela para a tela
 * e os exports (PDF/Excel) mostrarem exatamente a mesma coisa.
 */

export interface FluxoCat {
    nome: string;
    total: number[];
    subcategorias: { nome: string; valores: number[] }[];
}

export interface FluxoGrupo {
    total: number[];
    categorias: FluxoCat[];
}

export interface FluxoData {
    meses: string[];
    entradas: FluxoGrupo;
    saidas: FluxoGrupo;
    saldo: number[];
}

/** "Periodo exibido": o ano inteiro ou um mes (indice 0 a 11). */
export type PeriodoExibido = 'ano' | number;

export const soma = (valores: number[]) => valores.reduce((a, b) => a + b, 0);

/**
 * Meses que viram coluna. Um mes especifico mostra so ele. No ano inteiro, sem
 * "mostrar meses zerados", some o mes sem nenhuma entrada nem saida (basta um
 * real em qualquer um dos dois para ele aparecer).
 */
export function mesesVisiveis(
    fluxo: FluxoData,
    { periodo, mostrarZerados }: { periodo: PeriodoExibido; mostrarZerados: boolean },
): number[] {
    if (periodo !== 'ano') return [periodo];
    const todos = fluxo.meses.map((_, i) => i);
    if (mostrarZerados) return todos;
    return todos.filter((i) => (fluxo.entradas.total[i] ?? 0) !== 0 || (fluxo.saidas.total[i] ?? 0) !== 0);
}

/** Analise horizontal: o valor como % das entradas do mesmo mes. Sem entrada nao ha percentual. */
export function percentualDaReceita(valor: number, entradas: number): number | null {
    return entradas === 0 ? null : (valor / entradas) * 100;
}

/**
 * Comparacao com o mes anterior (Δ%). Janeiro nao tem anterior dentro do ano
 * consultado e mes anterior zerado nao tem base: nos dois casos, sem variacao.
 */
export function variacaoPercentual(atual: number, anterior: number | undefined): number | null {
    if (anterior === undefined || anterior === 0) return null;
    return ((atual - anterior) / Math.abs(anterior)) * 100;
}

export interface LinhaDoFluxo {
    key: string;
    nivel: 'grupo' | 'categoria' | 'subcategoria' | 'saldo';
    lado?: 'entrada' | 'saida';
    nome: string;
    valores: number[];
}

/**
 * Linhas da tabela, na ordem: Entradas, os grupos dela (e as categorias do grupo
 * aberto), Saidas, idem, e o Saldo. Categoria sem nenhum valor no ano nao entra.
 */
export function linhasDoFluxo(
    fluxo: FluxoData,
    rotulos: { entradas: string; saidas: string; saldo: string },
    aberta: (keyDoGrupo: string) => boolean,
): LinhaDoFluxo[] {
    const linhas: LinhaDoFluxo[] = [];
    const lado = (grupo: FluxoGrupo, prefixo: 'entrada' | 'saida', nome: string) => {
        linhas.push({ key: prefixo, nivel: 'grupo', lado: prefixo, nome, valores: grupo.total });
        for (const cat of grupo.categorias) {
            const key = `${prefixo}-${cat.nome}`;
            linhas.push({ key, nivel: 'categoria', lado: prefixo, nome: cat.nome, valores: cat.total });
            if (!aberta(key)) continue;
            for (const sub of cat.subcategorias) {
                if (!sub.valores.some((v) => v !== 0)) continue;
                linhas.push({ key: `${key}-${sub.nome}`, nivel: 'subcategoria', lado: prefixo, nome: sub.nome, valores: sub.valores });
            }
        }
    };
    lado(fluxo.entradas, 'entrada', rotulos.entradas);
    lado(fluxo.saidas, 'saida', rotulos.saidas);
    linhas.push({ key: 'saldo', nivel: 'saldo', nome: rotulos.saldo, valores: fluxo.saldo });
    return linhas;
}
